import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Wrench, 
  HelpCircle, 
  Users, 
  Settings, 
  LogOut,
  Menu,
  X,
  Bell,
  Search,
  Share2,
  User
} from 'lucide-react';
import { adminGetStats } from '../lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1ab805ea-4762-44e6-9a14-41ceae068a7b/artifacts/8u7v0lx3_logo.jpeg";

export default function AdminLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({ new_leads: 0 });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('ad_token');
    const userData = localStorage.getItem('ad_user');
    
    if (!token || !userData) {
      navigate('/admin/login');
      return;
    }
    
    setUser(JSON.parse(userData));
    
    // Fetch stats for notifications
    const fetchStats = async () => {
      try {
        const res = await adminGetStats();
        setStats(res.data);
      } catch (err) {
        console.error('Error fetching stats:', err);
      }
    };
    fetchStats();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('ad_token');
    localStorage.removeItem('ad_user');
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin', label: 'Paneli Kryesor', icon: LayoutDashboard, exact: true },
    { path: '/admin/services', label: 'Shërbimet', icon: Wrench },
    { path: '/admin/faqs', label: 'FAQ', icon: HelpCircle },
    { path: '/admin/leads', label: 'Kërkesat', icon: Users, badge: stats.new_leads },
    { path: '/admin/social-media', label: 'Rrjetet Sociale', icon: Share2 },
    { path: '/admin/seo', label: 'SEO & Meta', icon: Search },
    { path: '/admin/settings', label: 'Konfigurimet', icon: Settings },
    { path: '/admin/profile', label: 'Profili Im', icon: User },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 admin-sidebar transform transition-transform duration-300 lg:translate-x-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-[#262626]">
            <Link to="/admin" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="A&D Service" className="h-12 w-auto brightness-0 invert" />
            </Link>
            <p className="text-[#71717A] text-xs mt-2">Paneli i Administrimit</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-6 px-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`admin-link flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium ${
                  isActive(item.path, item.exact)
                    ? 'bg-[#D4B982]/20 text-[#D4B982]'
                    : 'text-[#71717A] hover:text-white'
                }`}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <item.icon size={18} />
                {item.label}
                {item.badge > 0 && (
                  <span className="ml-auto bg-[#D4B982] text-[#0A0A0A] text-xs font-bold px-2 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-[#262626]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#D4B982] flex items-center justify-center text-[#0A0A0A] font-bold">
                {user.username?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-white text-sm font-medium">{user.username}</p>
                <p className="text-[#71717A] text-xs capitalize">{user.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#71717A] hover:text-[#EF4444] transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut size={16} />
              Dilni
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-[#E4E4E7] px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden p-2 text-[#0A0A0A]"
              data-testid="admin-menu-button"
            >
              {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            <div className="flex-1 lg:flex-none">
              <h1 className="text-lg font-semibold text-[#0A0A0A]">
                {navItems.find(item => isActive(item.path, item.exact))?.label || 'Admin'}
              </h1>
            </div>

            <div className="flex items-center gap-4">
              {stats.new_leads > 0 && (
                <Link 
                  to="/admin/leads" 
                  className="relative p-2 text-[#71717A] hover:text-[#0A0A0A] transition-colors"
                >
                  <Bell size={20} />
                  <span className="absolute top-0 right-0 w-5 h-5 bg-[#D4B982] text-[#0A0A0A] text-xs font-bold rounded-full flex items-center justify-center">
                    {stats.new_leads}
                  </span>
                </Link>
              )}
              <Link 
                to="/" 
                target="_blank"
                className="text-sm text-[#71717A] hover:text-[#D4B982] transition-colors"
              >
                Shiko Faqen →
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
